import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { GET_PUSH_HISTORY } from './enterprise-push-history.gql';
import { GetPushHistory, GetPushHistoryVariables } from './__generated__/GetPushHistory';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tableHead: {
      '& th': {
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        color: theme.color.grey.neutral200,
        borderBottom: '1px solid #e0e0e0',
      },
    },
    subName: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    cellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
    },
    mutedCellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    statusChip: {
      display: 'inline-block',
      borderRadius: 11,
      padding: '2px 10px',
      fontSize: '12px',
      fontWeight: 600,
      textTransform: 'capitalize' as const,
    },
    statusAccepted: { backgroundColor: '#e8f5e9', color: '#2e7d32' },
    statusPending: { backgroundColor: '#fff0e0', color: '#c77700' },
    statusRejected: { backgroundColor: '#fdecea', color: '#c62828' },
    statusPartial: { backgroundColor: '#e8eaf6', color: '#3949ab' },
    statusSuperseded: { backgroundColor: '#f2f2f2', color: '#646464' },
  })
);

interface Props {
  subTenantId?: string;
  showSubAccount?: boolean;
  emptyMessage: string;
}

export const PushHistoryTable = (props: Props) => {
  const { subTenantId, showSubAccount = true, emptyMessage } = props;
  const classes = useStyles();

  const { data, loading } = useCampfireQuery<GetPushHistory, GetPushHistoryVariables>(GET_PUSH_HISTORY, {
    options: { variables: { subTenantId: subTenantId ?? null } },
  });

  const entries = data?.tm.enterprisePushHistory ?? [];

  const statusClass = (status: string) => {
    if (status === 'accepted') return classes.statusAccepted;
    if (status === 'rejected') return classes.statusRejected;
    if (status === 'partial') return classes.statusPartial;
    if (status === 'superseded') return classes.statusSuperseded;
    return classes.statusPending;
  };

  if (!loading && entries.length === 0) {
    return (
      <Box paddingTop='1.5rem'>
        <Typography variant='body2' className={classes.bodyPanel}>
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer style={{ marginTop: '1rem' }}>
      <Table>
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell>Date</TableCell>
            {showSubAccount && <TableCell>Sub-Account</TableCell>}
            <TableCell>Pushed</TableCell>
            <TableCell>Changes</TableCell>
            <TableCell align='right'>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={`${entry.subTenantId}-${entry.datePushed}`} hover>
              <TableCell>
                <Typography className={classes.cellText}>
                  {unpackToDateTime(entry.datePushed).toFormat('d MMM y')}
                </Typography>
              </TableCell>
              {showSubAccount && (
                <TableCell>
                  <Typography className={classes.subName}>{entry.subAccountName}</Typography>
                </TableCell>
              )}
              <TableCell>
                <Typography className={classes.cellText}>{entry.entitySummary}</Typography>
              </TableCell>
              <TableCell>
                <Typography className={classes.mutedCellText}>
                  {`${entry.changeCount} change${entry.changeCount === 1 ? '' : 's'}`}
                  {entry.pendingCount > 0 ? ` · ${entry.pendingCount} awaiting review` : ''}
                </Typography>
              </TableCell>
              <TableCell align='right'>
                <span className={`${classes.statusChip} ${statusClass(entry.status)}`}>{entry.status}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
